import type { Type } from '@angular/core';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
} from '@angular/core';

import { DynamicModule } from 'ng-dynamic-component';

import { FeatureSplit as FeatureSplitComponent } from '@valk-nx/components/ui-feature-split/src/lib/feature-split';
import type { PhotoInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import { AnchorComponent } from '@valk-nx/storyblok/components/anchor/src/lib/anchor';
import { LinkListComponent as SbLinkListComponent } from '@valk-nx/storyblok/components/link-list/src/lib/link-list';
import { RichTextComponent } from '@valk-nx/storyblok/components/richtext/src/lib/rich-text';
import { StoryblokDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok.directive';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { StoryblokHelper } from '@valk-nx/storyblok-helpers/src/lib/general/storyblok.helper';
import type { FeatureSplit } from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

const Components: Record<string, Type<unknown>> = {
	button: AnchorComponent,
	linkList: SbLinkListComponent,
	richText: RichTextComponent,
};

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'sb-feature-split-section',
	templateUrl: './feature-split-section.html',
	imports: [DynamicModule, FeatureSplitComponent, StoryblokDirective],
})
export class StoryblokFeatureSplitComponent extends StoryblokRootDirective {
	components: typeof Components = Components;
	featureSplits = input.required<FeatureSplit[]>();
	startInversed = input(false);

	splits = computed(() =>
		this.featureSplits().map((featureSplit) => ({
			...featureSplit,
			content: featureSplit.content.map((blok) =>
				blok.component === 'button'
					? { ...blok, variant: 'underline' }
					: blok,
			),
			photos: this.mapPhotos(featureSplit),
		})),
	);

	private mapPhotos(featureSplit: FeatureSplit): PhotoInterface[] {
		if (!featureSplit.images?.length) {
			return [{ src: '' }];
		}

		return featureSplit.images.map((image) =>
			image.bynder?.length
				? StoryblokHelper.mapBynderToPhotoInterface(
						image.bynder[0],
						image.imageAlt,
					)
				: { src: '' },
		);
	}
}
